// メニューのカーソル送り。 全メニュー画面が共通で使う唯一の作法。
//   他ゲーム標準の「押した瞬間に1つ動く → 少し待つ → 以降は連続で送られる」を1箇所で実装する。
//   (2026-07-25: 従来はタイトル=ラッチ式 / 幕間=0.15秒連射 / ADV=毎tick と3通りに割れていた)

export type MenuNavState = {
  dir: -1 | 0 | 1      // いま押されている方向 (前 tick 時点)
  heldMs: number       // その方向を押し続けている時間 (リピート発火のたびに差し引く)
  repeated: boolean    // 一度でも自動リピートが発火したか (初回待ち→以降間隔の切替に使う)
}

export const MENU_NAV_INITIAL: MenuNavState = { dir: 0, heldMs: 0, repeated: false }

// 初回リピートまでの待ち。 人が「トン」と押す時間は概ね 150〜250ms なので、
//   それより十分長く取らないと「1つ動かしたいのに2〜3個飛ぶ」(2026-07-25 実測: 0.3秒押しで3個飛んだ)。
export const MENU_NAV_DELAY_MS = 400
// 自動リピートの間隔。 押しっぱなしで気持ちよく送れて、かつ行き過ぎない値。
export const MENU_NAV_REPEAT_MS = 120

/**
 * 1 tick 分のカーソル送りを計算する。
 * @param prev   前 tick の MenuNavState
 * @param dirNow いま押されている方向 (-1=上 / 0=なし / 1=下)。押しっぱなし判定に使う
 * @param navEdge この tick に「押された瞬間」が観測された方向 (-1/0/1)。無ければ 0
 * @param dtMs   経過ミリ秒 (呼び出し側は FIXED_DT * 1000 を渡す)
 * @returns move = このtickでカーソルを動かす量 (-1/0/1)
 */
export function stepMenuNav(
  prev: MenuNavState, dirNow: -1 | 0 | 1, navEdge: -1 | 0 | 1, dtMs: number,
): { next: MenuNavState, move: -1 | 0 | 1 } {
  // 1: 押した瞬間 (キーボードのエッジ) を最優先。 パッドの軸には無いので #3 が拾う。
  if (navEdge !== 0) {
    return { next: { dir: navEdge, heldMs: 0, repeated: false }, move: navEdge }
  }
  // 2: 何も押されていない → 白紙化
  if (dirNow === 0) {
    return { next: MENU_NAV_INITIAL, move: 0 }
  }
  // 3: 方向が変わった (主にパッドの軸: 押した瞬間はエッジが無いのでここで拾う)
  if (dirNow !== prev.dir) {
    return { next: { dir: dirNow, heldMs: 0, repeated: false }, move: dirNow }
  }
  // 4: 同じ方向を押し続けている → 初回待ち→以降は一定間隔でリピート
  const heldMs = prev.heldMs + dtMs
  const threshold = prev.repeated ? MENU_NAV_REPEAT_MS : MENU_NAV_DELAY_MS
  if (heldMs >= threshold) {
    return { next: { dir: prev.dir, heldMs: heldMs - threshold, repeated: true }, move: prev.dir }
  }
  return { next: { dir: prev.dir, heldMs, repeated: prev.repeated }, move: 0 }
}

/**
 * カーソル位置を動かす。
 * @param wrap true = 端で反対側へ巡回 / false = 端で止まる
 */
export function moveCursor(index: number, move: -1 | 0 | 1, count: number, wrap: boolean): number {
  if (count <= 0) return index
  if (move === 0) return index
  if (wrap) return (index + move + count) % count
  return Math.max(0, Math.min(count - 1, index + move))
}
